import React, { useState, useEffect, useRef } from 'react';
import { Image, Palette, Building2, Mail, Globe, Link, Key, AlertTriangle, Check, Save, MapPin, RefreshCw, ChevronDown, ChevronRight, Trash2, Zap, Lock, Clipboard, FolderOpen, CheckCircle, Clock, Lightbulb, Hotel, Utensils, HardHat, ShoppingBag, Heart, CalendarDays, Car, Settings2, House, BedDouble, HeadphonesIcon, Phone } from 'lucide-react';
import PhoneInput from '../components/PhoneInput';
import { useConfirm } from '../components/ConfirmModal';

const BASE_URL = 'https://qbdp-backend-production.up.railway.app/api';

const SECTEURS = [
  { value: 'hotel',            label: 'Hôtellerie / Hébergement / Camping',       Icon: Hotel },
  { value: 'hotel_restaurant', label: 'Hôtel Restaurant',                         Icon: Hotel },
  { value: 'restaurant',       label: 'Restauration / Bar / Café',                Icon: Utensils },
  { value: 'btp',          label: 'BTP / Menuiserie / Rénovation / Artisanat', Icon: HardHat },
  { value: 'commerce',     label: 'Commerce / Boutique / Retail',              Icon: ShoppingBag },
  { value: 'medical',      label: 'Médical / Santé / Bien-être / SPA',         Icon: Heart },
  { value: 'evenementiel', label: 'Événementiel / Traiteur / Location de salle', Icon: CalendarDays },
  { value: 'auto',         label: 'Automobile / Garage / Concessionnaire',     Icon: Car },
  { value: 'residence',    label: 'Résidences / Appart-hôtel',                 Icon: House },
  { value: 'logis',        label: 'Logis / Gîte / Chambre d\'hôtes',           Icon: BedDouble },
  { value: 'rbnb',         label: 'Location courte durée (Airbnb)',             Icon: BedDouble },
  { value: 'autre',        label: 'Autre activité',                            Icon: Settings2 },
];

function SecteurSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = SECTEURS.find(s => s.value === value) || SECTEURS[0];
  return (
    <div style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', textAlign: 'left' }}>
        <selected.Icon size={15} color="#6b7280" />
        <span style={{ flex: 1, color: '#111827' }}>{selected.label}</span>
        <ChevronDown size={14} color="#9ca3af" style={{ transition: 'transform .15s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setOpen(false)} />
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', zIndex: 100, marginTop: 4, overflow: 'hidden' }}>
            {SECTEURS.map(s => (
              <div key={s.value} onClick={() => { onChange(s.value); setOpen(false); }}
                style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 14px', cursor: 'pointer', background: value === s.value ? '#eff6ff' : '#fff', fontSize: 13,
                  borderLeft: value === s.value ? '2px solid #1a56db' : '2px solid transparent' }}
                onMouseEnter={e => { if (value !== s.value) e.currentTarget.style.background = '#f9fafb'; }}
                onMouseLeave={e => { if (value !== s.value) e.currentTarget.style.background = '#fff'; }}>
                <s.Icon size={14} color={value === s.value ? '#1a56db' : '#6b7280'} />
                <span style={{ flex: 1, color: value === s.value ? '#1a56db' : '#374151', fontWeight: value === s.value ? 600 : 400 }}>{s.label}</span>
                {value === s.value && <Check size={13} color="#1a56db" />}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const COULEURS_PRESET = [
  '#1a56db', '#7c3aed', '#db2777', '#dc2626',
  '#d97706', '#16a34a', '#0891b2', '#1a1a2e',
];

const DEFAULT_CODE = '(x1a5y0-f1k7K2u)';

function Section({ title, Icon, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 28, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #f3f4f6' }}>
        {Icon && <Icon size={20} color="#6b7280" />}
        <span style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

const inp = {
  width: '100%', padding: '10px 14px', borderRadius: 10,
  border: '1px solid #e5e7eb', fontSize: 13, outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit',
};

// ── CHAMP MOT DE PASSE AVEC ŒIL ───────────────────────────────────────────────
function PasswordField({ label, value, onChange, placeholder, hint }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>{label}</label>}
      <div style={{ position: 'relative' }}>
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder || '••••••••'}
          style={{ ...inp, paddingRight: 44 }}
        />
        <button
          type="button"
          onClick={() => setShow(v => !v)}
          style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', lineHeight: 0, padding: 0 }}>
          {show ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          )}
        </button>
      </div>
      {hint && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

// ── MODAL RESET ────────────────────────────────────────────────────────────────
function ModalReset({ onClose, onSuccess, showToast, isGod, currentUserId }) {
  const [code, setCode]         = useState('');
  const [loading, setLoading]   = useState(false);
  const [admins, setAdmins]     = useState([]);
  const [targetId, setTargetId] = useState('');
  const [action, setAction]     = useState('reset_perso');
  const token = sessionStorage.getItem('qbdp_token');

  useEffect(() => {
    if (isGod) {
      fetch(`${BASE_URL}/personnalisation/admins`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(d => { if (Array.isArray(d)) setAdmins(d); }).catch(() => {});
    }
  }, [isGod]);

  const ACTIONS = [
    { id: 'reset_perso',      label: 'Réinitialiser personnalisation', desc: 'Remet logo, couleurs, infos aux valeurs QBDP' },
    { id: 'reset_code',       label: 'Réinitialiser le code',         desc: 'Remet le code par défaut pour que le client le change' },
    { id: 'bloquer',          label: 'Bloquer le compte',             desc: 'Bloque l\'accès au dashboard et à l\'app' },
    { id: 'debloquer',        label: 'Débloquer le compte',           desc: 'Réactive l\'accès' },
    ...(isGod ? [{ id: 'supprimer_compte', label: '⚠ Supprimer le compte', desc: 'Suppression définitive et irréversible de tout le compte', danger: true }] : []),
  ];

  const handleReset = async () => {
    if (!code) { showToast('Entrez votre code', false); return; }
    if (action === 'supprimer_compte' && !targetId) { showToast('Sélectionne un compte cible à supprimer', false); return; }
    setLoading(true);
    try {
      const body = { code, action };
      if (isGod && targetId) body.target_admin_id = parseInt(targetId);
      const r = await fetch(`${BASE_URL}/personnalisation/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const d = await r.json();
      if (!r.ok) { showToast(d.error || 'Code incorrect', false); setLoading(false); return; }
      showToast('Action effectuée avec succès');
      onSuccess();
      onClose();
    } catch { showToast('Erreur réseau', false); }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}><Zap size={32} color="#7c3aed" /></div>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#1a1a2e', textAlign: 'center', marginBottom: 20 }}>
          {isGod ? 'God Mode — Administration' : 'Actions avancées'}
        </div>

        {/* Sélecteur compte — super-admin uniquement */}
        {isGod && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Compte cible</label>
            <select value={targetId} onChange={e => setTargetId(e.target.value)} style={{ ...inp }}>
              <option value="" disabled={action === 'supprimer_compte'}>ADMINISTRATEUR QBDP</option>
              {admins
                .filter(a => a.id !== currentUserId)
                .map(a => (
                  <option key={a.id} value={a.id}>
                    {a.nom} — {a.societe}
                    {a.compte_bloque ? ' [Bloqué]' : ''}
                    {a.code_par_defaut ? ' [Code défaut]' : ''}
                    {a.a_logo ? ' [Logo]' : ''}
                  </option>
                ))}
            </select>
          </div>
        )}

        {/* Sélecteur d'action — tous les utilisateurs */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 8 }}>Action</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ACTIONS.map(a => (
              <div key={a.id} onClick={() => setAction(a.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                  border: `2px solid ${action === a.id ? (a.danger ? '#7f1d1d' : '#1a56db') : (a.danger ? '#fecaca' : '#e5e7eb')}`,
                  background: action === a.id ? (a.danger ? '#fef2f2' : '#eff6ff') : (a.danger ? '#fff5f5' : '#fff') }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: action === a.id ? 600 : 400, color: a.danger ? '#7f1d1d' : 'inherit' }}>{a.label}</div>
                  <div style={{ fontSize: 11, color: a.danger ? '#dc2626' : '#9ca3af' }}>{a.desc}</div>
                </div>
                {action === a.id && <Check size={14} color={a.danger ? '#7f1d1d' : '#1a56db'} />}
              </div>
            ))}
          </div>
        </div>

        {/* Code */}
        <PasswordField
          label={isGod ? 'Votre code God Mode' : 'Votre code de réinitialisation'}
          value={code}
          onChange={e => setCode(e.target.value)}
        />

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>
            Annuler
          </button>
          <button onClick={handleReset} disabled={loading}
            style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none',
              background: action === 'supprimer_compte' ? '#7f1d1d' : action === 'bloquer' ? '#dc2626' : action === 'debloquer' ? '#16a34a' : '#1a56db',
              color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {loading ? 'Confirmation...' : 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MODAL CHANGER CODE ─────────────────────────────────────────────────────────
function ModalChangerCode({ onClose, showToast }) {
  const [ancien, setAncien]   = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const token = sessionStorage.getItem('qbdp_token');

  const handleSave = async () => {
    if (!ancien) { showToast('Entrez votre code actuel', false); return; }
    if (nouveau.length < 4) { showToast('Le nouveau code doit faire au moins 4 caractères', false); return; }
    if (nouveau !== confirm) { showToast('Les codes ne correspondent pas', false); return; }
    setLoading(true);
    try {
      const r = await fetch(`${BASE_URL}/auth/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: ancien, new_password: nouveau })
      });
      const d = await r.json();
      if (!r.ok) { showToast(d.error || 'Erreur', false); setLoading(false); return; }
      showToast('Code mis à jour !');
      onClose();
    } catch { showToast('Erreur réseau', false); }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}><Key size={32} color="#1a56db" /></div>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#1a1a2e', textAlign: 'center', marginBottom: 16 }}>
          Changer le code de connexion
        </div>

        <PasswordField label="Code actuel" value={ancien} onChange={e => setAncien(e.target.value)} placeholder="Votre code actuel" />
        <PasswordField label="Nouveau code" value={nouveau} onChange={e => setNouveau(e.target.value)} placeholder="Min. 4 caractères" />
        <PasswordField label="Confirmer le nouveau code" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Répéter le code" />

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>
            Annuler
          </button>
          <button onClick={handleSave} disabled={loading}
            style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: '#1a56db', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            {loading ? 'Enregistrement...' : <><Save size={13} /> Enregistrer</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PERSONNALISATION ───────────────────────────────────────────────────────────
export default function Personnalisation() {
  const [params, setParams]       = useState({
    logo_base64: '', logo_mobile_base64: '', logo_url: '', couleur_principale: '#1a56db', couleur_secondaire: '#1e40af', couleur_texte_nfc: '#ffffff', couleur_texte_entete: '#ffffff',
    nom_etablissement: '', adresse_etablissement: '', code_postal: '', ville_etablissement: '', telephone_etablissement: '',
    message_au_revoir: '', message_retour: '',
    site_web: '', email_expediteur: '', nom_expediteur: '', message_accueil: '',
    reset_code: DEFAULT_CODE, secteur_activite: 'hotel', heure_checkin: '14:00',
  });
  const [logoNew, setLogoNew]             = useState(null); // file1 pending upload
  const [logoMobileNew, setLogoMobileNew] = useState(null); // file2 pending upload
  const [logoSrc, setLogoSrc] = useState({ web: 'file1', mobile: 'file2', email: 'url' });
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState(null);
  const [showReset, setShowReset]   = useState(false);
  const [showCode, setShowCode]     = useState(false);
  const [showGodMode, setShowGodMode] = useState(false);
  const [customDomain, setCustomDomain] = useState(null); // { domain, status, records, verified }
  const [newDomain, setNewDomain]   = useState('');
  const [domainLoading, setDomainLoading] = useState(false);
  const [geocodeLoading, setGeocodeLoading] = useState(false);
  const [geocodeResult, setGeocodeResult]   = useState(null);
  const [webhook, setWebhook]               = useState(null);  // { token, welcome_email_enabled }
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [showSnippet, setShowSnippet]       = useState(false);
  const [copied, setCopied]                 = useState('');
  const { showConfirm, confirmModal } = useConfirm();

  // ref always holds the latest values for auto-save on unmount
  const autoSaveRef = useRef({ params, logoNew, logoMobileNew, logoSrc });
  useEffect(() => { autoSaveRef.current = { params, logoNew, logoMobileNew, logoSrc }; }, [params, logoNew, logoMobileNew, logoSrc]);

  // auto-save when navigating away (component unmounts)
  useEffect(() => {
    return () => {
      const { params: p, logoNew: ln, logoMobileNew: lmn, logoSrc: ls } = autoSaveRef.current;
      const tk = sessionStorage.getItem('qbdp_token');
      if (!tk) return;
      if (ln) {
        fetch(`${BASE_URL}/personnalisation/logo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
          body: JSON.stringify({ logo_base64: ln }),
        }).catch(() => {});
      }
      if (lmn) {
        fetch(`${BASE_URL}/personnalisation/logo-mobile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
          body: JSON.stringify({ logo_mobile_base64: lmn }),
        }).catch(() => {});
      }
      fetch(`${BASE_URL}/personnalisation/parametres`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ ...p, logo_mobile_src: ls.mobile, logo_web_src: ls.web, logo_email_src: ls.email }),
      }).catch(() => {});
    };
  }, []); // empty deps = run cleanup only on unmount

  const token   = sessionStorage.getItem('qbdp_token');
  const userRaw = sessionStorage.getItem('qbdp_user');
  const user    = userRaw ? JSON.parse(userRaw) : {};
  const isGod   = user?.role === 'super_admin' || user?.email === 'oliviergueroult@qbdp.fr';
  const isDefault = params.reset_code === DEFAULT_CODE;

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const load = () => {
    fetch(`${BASE_URL}/personnalisation/parametres`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        setParams(p => ({ ...p, ...d }));
        setLogoSrc({
          web:    d.logo_web_src    || 'file1',
          mobile: d.logo_mobile_src || 'file2',
          email:  d.logo_email_src  || 'url',
        });
      })
      .catch(() => {});
  };

  useEffect(() => { load(); loadDomain(); loadWebhook(); }, []);

  const loadDomain = () => {
    fetch(`${BASE_URL}/personnalisation/custom-domain`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.domain) setCustomDomain(d); })
      .catch(() => {});
  };

  const handleAddDomain = async () => {
    const domain = newDomain.trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    if (!domain) return;
    setDomainLoading(true);
    try {
      const r = await fetch(`${BASE_URL}/personnalisation/custom-domain`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ domain }),
      }).then(r => r.json());
      if (r.error) { showToast(r.error, false); }
      else { setCustomDomain(r); setNewDomain(''); showToast('Domaine ajouté — ajoutez maintenant les enregistrements DNS'); }
    } finally { setDomainLoading(false); }
  };

  const handleVerifyDomain = async () => {
    setDomainLoading(true);
    try {
      const r = await fetch(`${BASE_URL}/personnalisation/custom-domain/verify`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json());
      setCustomDomain(prev => ({ ...prev, status: r.status, verified: r.verified }));
      showToast(r.verified ? 'Domaine vérifié !' : 'Pas encore vérifié — attendez quelques minutes et réessayez', r.verified);
    } finally { setDomainLoading(false); }
  };

  const handleDeleteDomain = async () => {
    const ok = await showConfirm({
      title: 'SUPPRIMER CE DOMAINE',
      subtitle: customDomain?.domain,
      body: 'Les emails reprendront depuis notifications@qbdp.fr.',
      confirmLabel: 'Supprimer',
      confirmColor: '#dc2626',
      icon: Trash2,
    });
    if (!ok) return;
    setDomainLoading(true);
    try {
      await fetch(`${BASE_URL}/personnalisation/custom-domain`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setCustomDomain(null); setNewDomain('');
      showToast('Domaine supprimé');
    } finally { setDomainLoading(false); }
  };

  useEffect(() => {
    let seq = '';
    const handler = (e) => {
      seq += e.key;
      if (seq.length > 3) seq = seq.slice(-3);
      if (seq === 'GOD') { setShowGodMode(true); seq = ''; }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const set = (k, v) => setParams(p => ({ ...p, [k]: v }));

  const handleLogoFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2000000) { showToast('Image trop lourde (max 2Mo)', false); return; }
    const reader = new FileReader();
    reader.onload = () => { setLogoNew(reader.result); set('logo_base64', reader.result); };
    reader.readAsDataURL(file);
  };

  const handleDeleteLogo = async () => {
    const ok = await showConfirm({
      title: 'SUPPRIMER LE LOGO',
      subtitle: 'Logo web',
      body: 'Le logo sera définitivement supprimé.',
      confirmLabel: 'Supprimer',
      confirmColor: '#dc2626',
      icon: Trash2,
    });
    if (!ok) return;
    await fetch(`${BASE_URL}/personnalisation/logo`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    setLogoNew(null); set('logo_base64', '');
    showToast('Logo web supprimé');
  };

  const handleLogoMobileFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2000000) { showToast('Image trop lourde (max 2Mo)', false); return; }
    const reader = new FileReader();
    reader.onload = () => { setLogoMobileNew(reader.result); set('logo_mobile_base64', reader.result); };
    reader.readAsDataURL(file);
  };

  const handleDeleteLogoMobile = async () => {
    const ok = await showConfirm({
      title: 'SUPPRIMER LE LOGO MOBILE',
      subtitle: 'Logo mobile',
      body: 'Le logo mobile sera définitivement supprimé.',
      confirmLabel: 'Supprimer',
      confirmColor: '#dc2626',
      icon: Trash2,
    });
    if (!ok) return;
    await fetch(`${BASE_URL}/personnalisation/logo-mobile`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    setLogoMobileNew(null); set('logo_mobile_base64', '');
    showToast('Logo mobile supprimé');
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  const loadWebhook = () => {
    fetch(`${BASE_URL}/webhooks/config`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setWebhook(d)).catch(() => {});
  };

  const handleRegenerateToken = async () => {
    const ok = await showConfirm({
      title: 'REGÉNÉRER LE TOKEN',
      subtitle: 'Webhook API',
      body: 'L\'ancien token sera immédiatement invalidé. Mettez à jour toutes vos intégrations.',
      confirmLabel: 'Regénérer',
      confirmColor: '#f59e0b',
      icon: RefreshCw,
    });
    if (!ok) return;
    setWebhookLoading(true);
    try {
      const r = await fetch(`${BASE_URL}/webhooks/token/regenerate`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setWebhook(w => ({ ...w, token: d.token }));
      showToast('Token regénéré');
    } catch { showToast('Erreur', false); }
    setWebhookLoading(false);
  };

  const handleToggleWelcome = async (val) => {
    setWebhook(w => ({ ...w, welcome_email_enabled: val }));
    await fetch(`${BASE_URL}/webhooks/config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ welcome_email_enabled: val }),
    }).catch(() => {});
  };

  const handleGeocodeAll = async () => {
    setGeocodeLoading(true);
    setGeocodeResult(null);
    try {
      const r = await fetch(`${BASE_URL}/clients/geocode-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (d.success) {
        setGeocodeResult({ ok: true, msg: `${d.geocoded} client${d.geocoded > 1 ? 's' : ''} géocodé${d.geocoded > 1 ? 's' : ''} sur ${d.total}` });
      } else {
        setGeocodeResult({ ok: false, msg: d.error || 'Erreur' });
      }
    } catch { setGeocodeResult({ ok: false, msg: 'Erreur réseau' }); }
    setGeocodeLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (logoNew) {
        await fetch(`${BASE_URL}/personnalisation/logo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ logo_base64: logoNew }),
        });
      }
      if (logoMobileNew) {
        await fetch(`${BASE_URL}/personnalisation/logo-mobile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ logo_mobile_base64: logoMobileNew }),
        });
      }
      const r = await fetch(`${BASE_URL}/personnalisation/parametres`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...params, logo_mobile_src: logoSrc.mobile, logo_web_src: logoSrc.web, logo_email_src: logoSrc.email }),
      });
      const d = await r.json();
      if (d.success) showToast('Paramètres sauvegardés !');
      else showToast('Erreur lors de la sauvegarde', false);
    } catch { showToast('Erreur de connexion', false); }
    setSaving(false);
  };

  const Preview = () => {
    const mobileLogo = logoSrc.mobile === 'file2' ? (logoMobileNew || params.logo_mobile_base64)
                     : logoSrc.mobile === 'url'   ? params.logo_url
                     : (logoNew || params.logo_base64);
    const cEntete   = params.couleur_principale   || '#1a56db';
    const cCentrale = params.couleur_secondaire   || '#1e40af';
    const ctEntete  = params.couleur_texte_entete || '#ffffff';
    const ctCentral = params.couleur_texte_nfc    || '#ffffff';
    const nom = params.nom_etablissement || 'Votre établissement';
    return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', background: '#f4f5f7', padding: '48px 12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>

      {/* Carte en-tête */}
      <div style={{ background: cEntete, borderRadius: 16, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {mobileLogo
            ? <img src={mobileLogo} alt="logo" style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'contain' }} />
            : <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(128,128,128,0.15)', border: `1px solid ${ctEntete}`, opacity: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Building2 size={20} color={ctEntete} /></div>
          }
          <div style={{ flex: 1, color: ctEntete, fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Bienvenue, Client · Ch. 101
          </div>
        </div>
        <div style={{ color: ctEntete, opacity: 0.65, fontSize: 11, marginTop: 8, paddingLeft: 56 }}>
          Séjour jusqu'au 10 mai
        </div>
      </div>

      {/* Carte centrale NFC */}
      <div style={{ background: cCentrale, borderRadius: 16, padding: '28px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
        <div style={{ color: ctCentral, opacity: 0.85, fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>Approchez du lecteur</div>
        {mobileLogo
          ? <img src={mobileLogo} alt="logo" style={{ width: 72, height: 72, objectFit: 'contain' }} />
          : <div style={{ width: 72, height: 72, borderRadius: 36, background: 'rgba(128,128,128,0.15)', border: `2px solid ${ctCentral}`, opacity: 0.9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: ctCentral, fontWeight: 800, fontSize: 22 }}>{nom[0]}</span>
            </div>
        }
        <div style={{ color: ctCentral, opacity: 0.85, fontSize: 10, fontWeight: 700, letterSpacing: 1.5 }}>OU CLIQUEZ</div>
      </div>

    </div>
    );
  };

  return (
    <div style={{ padding: 32, maxWidth: 1000, margin: '0 auto', paddingBottom: 100 }}>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 2000, padding: '12px 20px', borderRadius: 12,
          background: toast.ok ? '#f0fdf4' : '#fef2f2', color: toast.ok ? '#16a34a' : '#dc2626',
          border: `1px solid ${toast.ok ? '#bbf7d0' : '#fecaca'}`, fontWeight: 600, fontSize: 13,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          {toast.msg}
        </div>
      )}

      {/* Modals */}
      {showReset && <ModalReset onClose={() => setShowReset(false)} onSuccess={load} showToast={showToast} isGod={isGod} currentUserId={user?.id} />}
      {showCode  && <ModalChangerCode onClose={() => { setShowCode(false); load(); }} showToast={showToast} />}

      {/* Bannière rouge si code par défaut — masquée pour super_admin */}
      {isDefault && user?.role !== 'super_admin' && user?.email !== 'oliviergueroult@qbdp.fr' && (
        <div style={{ background: '#fef2f2', border: '2px solid #dc2626', borderRadius: 12, padding: '14px 20px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 14, color: '#991b1b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={16} /> VEUILLEZ CHANGER VOTRE CODE DE RÉINITIALISATION
          </div>
          <button onClick={() => setShowCode(true)}
            style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0, marginLeft: 16 }}>
            Changer maintenant →
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}><Palette size={22} /> Personnalisation</h1>
        <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Configurez l'identité visuelle et les informations de votre établissement</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24 }}>
        <div>

          {/* Logo */}
          <Section title="Logos" Icon={Image}>
            {(() => {
              const dropzone = (id, previewSrc, onFile, onDelete) => (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {previewSrc
                    ? <>
                        <img src={previewSrc} alt="logo" style={{ height: 44, maxWidth: 120, objectFit: 'contain', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', padding: 4 }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <button onClick={() => document.getElementById(id).click()}
                            style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>
                            Changer
                          </button>
                          <button onClick={onDelete}
                            style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', color: '#991b1b' }}>
                            Supprimer
                          </button>
                        </div>
                      </>
                    : <div onClick={() => document.getElementById(id).click()}
                        style={{ border: '2px dashed #e5e7eb', borderRadius: 8, padding: '10px 20px', textAlign: 'center', cursor: 'pointer', transition: 'border-color .15s', display: 'flex', alignItems: 'center', gap: 8 }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = '#1a56db'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = '#e5e7eb'}>
                        <FolderOpen size={16} color="#9ca3af" />
                        <span style={{ fontSize: 12, color: '#6b7280' }}>Choisir un fichier — PNG · JPG · PDF</span>
                      </div>
                  }
                  <input id={id} type="file" accept="image/*,.pdf" onChange={onFile} style={{ display: 'none' }} />
                </div>
              );

              const srcPreview = (src) => {
                if (src === 'file1') return logoNew || params.logo_base64 || null;
                if (src === 'file2') return logoMobileNew || params.logo_mobile_base64 || null;
                if (src === 'url')   return params.logo_url || null;
                return null;
              };

              const DESTINATIONS = [
                { key: 'mobile', label: 'App mobile', desc: 'Affiché dans l\'application sur téléphone de vos clients et employés', icon: '📱' },
                { key: 'web',    label: 'Interface admin',  desc: 'Affiché en haut à gauche de votre tableau de bord qbdp',          icon: '🖥️' },
                { key: 'email',  label: 'Emails envoyés',   desc: 'Inséré dans les emails d\'invitation et de communication',          icon: '✉️' },
              ];
              const SOURCES = [
                { key: 'file1', label: 'Logo 1' },
                { key: 'file2', label: 'Logo 2' },
                { key: 'url',   label: 'URL' },
              ];

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                  {/* Upload des fichiers */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ background: '#1a56db', color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0 }}>1</span>
                        Logo principal
                      </div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>Votre logo en haute définition</div>
                      {dropzone('logo-input', logoNew || params.logo_base64 || null, handleLogoFile, handleDeleteLogo)}
                    </div>
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ background: '#6b7280', color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0 }}>2</span>
                        Logo alternatif
                        <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400 }}>optionnel</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>Version différente ou fond transparent</div>
                      {dropzone('logo-mobile-input', logoMobileNew || params.logo_mobile_base64 || null, handleLogoMobileFile, handleDeleteLogoMobile)}
                    </div>
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Link size={13} color="#6b7280" />
                        Logo via URL
                        <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400 }}>optionnel</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>Logo déjà hébergé sur votre site web</div>
                      {params.logo_url && (
                        <img src={params.logo_url} alt="logo url"
                          style={{ height: 44, maxWidth: 120, objectFit: 'contain', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', padding: 4 }}
                          onError={e => e.target.style.display = 'none'} />
                      )}
                      <input type="url" value={params.logo_url || ''} onChange={e => set('logo_url', e.target.value)}
                        placeholder="https://monsite.fr/logo.png"
                        style={{ ...inp, fontSize: 11 }} />
                    </div>
                  </div>

                  {/* Attribution par destination */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quel logo apparaît où ?</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {DESTINATIONS.map(dest => {
                        const currentSrc = logoSrc[dest.key];
                        const preview = srcPreview(currentSrc);
                        return (
                          <div key={dest.key} style={{ display: 'flex', alignItems: 'center', gap: 14, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 16px' }}>
                            <div style={{ minWidth: 200 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>{dest.icon}</span> {dest.label}
                              </div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{dest.desc}</div>
                            </div>
                            <div style={{ display: 'flex', gap: 6, flex: 1 }}>
                              {SOURCES.map(src => {
                                const active = currentSrc === src.key;
                                const hasContent = srcPreview(src.key);
                                return (
                                  <label key={src.key} style={{
                                    display: 'flex', alignItems: 'center', gap: 6, cursor: hasContent ? 'pointer' : 'not-allowed',
                                    padding: '6px 12px', borderRadius: 8, border: `1.5px solid ${active ? '#1a56db' : '#e5e7eb'}`,
                                    background: active ? '#eff6ff' : '#fff', fontSize: 12,
                                    fontWeight: active ? 700 : 500,
                                    color: active ? '#1a56db' : hasContent ? '#374151' : '#d1d5db',
                                    opacity: hasContent ? 1 : 0.5,
                                  }}>
                                    <input type="radio" name={`logo-${dest.key}`} checked={active}
                                      onChange={() => hasContent && setLogoSrc(prev => ({ ...prev, [dest.key]: src.key }))}
                                      style={{ accentColor: '#1a56db', width: 13, height: 13 }}
                                    />
                                    {src.label}
                                    {active && hasContent && (
                                      <img src={preview} alt="" style={{ height: 20, maxWidth: 40, objectFit: 'contain', marginLeft: 4 }} onError={() => {}} />
                                    )}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                </div>
              );
            })()}
          </Section>

          {/* Couleurs */}
          <Section title="Couleurs" Icon={Palette}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <Field label="Couleur en-tête" hint="Fond de la carte d'accueil (haut)">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="color" value={params.couleur_principale} onChange={e => set('couleur_principale', e.target.value)}
                    style={{ width: 40, height: 36, borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer', padding: 2 }} />
                  <input style={{ ...inp, flex: 1 }} value={params.couleur_principale} onChange={e => set('couleur_principale', e.target.value)} />
                </div>
              </Field>
              <Field label="Couleur centrale" hint="Fond de la carte d'accès NFC (milieu)">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="color" value={params.couleur_secondaire} onChange={e => set('couleur_secondaire', e.target.value)}
                    style={{ width: 40, height: 36, borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer', padding: 2 }} />
                  <input style={{ ...inp, flex: 1 }} value={params.couleur_secondaire} onChange={e => set('couleur_secondaire', e.target.value)} />
                </div>
              </Field>
              <Field label="Couleur texte en-tête" hint="Texte sur la carte d'accueil">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="color" value={params.couleur_texte_entete || '#ffffff'} onChange={e => set('couleur_texte_entete', e.target.value)}
                    style={{ width: 40, height: 36, borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer', padding: 2 }} />
                  <input style={{ ...inp, flex: 1 }} value={params.couleur_texte_entete || '#ffffff'} onChange={e => set('couleur_texte_entete', e.target.value)} />
                </div>
              </Field>
              <Field label="Couleur texte central" hint='"Approchez du lecteur" et "OU CLIQUEZ"'>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="color" value={params.couleur_texte_nfc || '#ffffff'} onChange={e => set('couleur_texte_nfc', e.target.value)}
                    style={{ width: 40, height: 36, borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer', padding: 2 }} />
                  <input style={{ ...inp, flex: 1 }} value={params.couleur_texte_nfc || '#ffffff'} onChange={e => set('couleur_texte_nfc', e.target.value)} />
                </div>
              </Field>
            </div>
          </Section>

          {/* Identité */}
          <Section title="Identité de l'établissement" Icon={Building2}>
            <Field label="Secteur d'activité" hint="Personalise le Copilote IA selon votre métier">
              <SecteurSelect value={params.secteur_activite || 'hotel'} onChange={v => set('secteur_activite', v)} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Field label="Nom de l'établissement">
                <input style={inp} placeholder="Ex: Hôtel Bellevue" value={params.nom_etablissement || ''} onChange={e => set('nom_etablissement', e.target.value)} />
              </Field>
              <Field label="Téléphone">
                <PhoneInput value={params.telephone_etablissement || ''} onChange={v => set('telephone_etablissement', v)} />
              </Field>
            </div>
            <Field label="Adresse">
              <input style={inp} placeholder="12 rue de la Paix" value={params.adresse_etablissement || ''} onChange={e => set('adresse_etablissement', e.target.value)} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 16 }}>
              <Field label="Code postal">
                <input style={inp} placeholder="75 001" value={params.code_postal || ''} onChange={e => { const d = e.target.value.replace(/\D/g,'').slice(0,5); set('code_postal', d.length > 2 ? d.slice(0,2)+' '+d.slice(2) : d); }} />
              </Field>
              <Field label="Ville">
                <input style={inp} placeholder="Paris" value={params.ville_etablissement || ''} onChange={e => set('ville_etablissement', e.target.value)} />
              </Field>
            </div>
            <Field label="Site web">
              <input style={inp} placeholder="https://www.monhotel.fr" value={params.site_web || ''} onChange={e => set('site_web', e.target.value)} />
            </Field>
            <Field label="Message d'accueil" hint="Affiché à l'ouverture de l'app">
              <input style={inp} placeholder="Bienvenue dans notre établissement !" value={params.message_accueil || ''} onChange={e => set('message_accueil', e.target.value)} />
            </Field>
            <Field label="Message au départ" hint="Affiché après le check-out">
              <input style={inp} placeholder="À très bientôt ! Nous espérons vous avoir comblé..." value={params.message_au_revoir || ''} onChange={e => set('message_au_revoir', e.target.value)} />
            </Field>
            <Field label="Message de retour" hint="Affiché pour un client qui revient">
              <input style={inp} placeholder="Ravi de vous revoir parmi nous !" value={params.message_retour || ''} onChange={e => set('message_retour', e.target.value)} />
            </Field>
            <Field label="Heure de check-in" hint="Activation automatique des accès à cette heure le jour d'arrivée">
              <input type="time" style={{ ...inp, width: 140 }} value={params.heure_checkin || '14:00'} onChange={e => set('heure_checkin', e.target.value)} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Field label="Nom expéditeur emails">
                <input style={inp} placeholder="Votre établissement" value={params.nom_expediteur || ''} onChange={e => set('nom_expediteur', e.target.value)} />
              </Field>
              <Field label="Email de réponse">
                <input style={inp} placeholder="contact@monhotel.fr" value={params.email_expediteur || ''} onChange={e => set('email_expediteur', e.target.value)} />
              </Field>
            </div>
          </Section>

          {/* Email */}
          {/* Domaine d'envoi personnalisé */}
          <Section title="Domaine d'envoi personnalisé" Icon={Globe}>
            {!customDomain ? (
              <div>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
                  Permettez à vos emails de partir de <strong>votre propre domaine</strong> (ex: notifications@monhotel.fr). Nécessite d'ajouter 3 enregistrements DNS chez votre hébergeur.
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <input
                    style={{ ...inp, flex: 1 }}
                    placeholder="monhotel.fr"
                    value={newDomain}
                    onChange={e => setNewDomain(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddDomain()}
                  />
                  <button onClick={handleAddDomain} disabled={domainLoading || !newDomain.trim()}
                    style={{ background: '#1e3a8a', color: 'white', border: 'none', borderRadius: 8, padding: '0 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: domainLoading ? 0.6 : 1 }}>
                    {domainLoading ? '...' : 'Configurer'}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {/* Statut */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: '10px 14px', background: customDomain.verified ? '#f0fdf4' : '#fffbeb', border: `1px solid ${customDomain.verified ? '#bbf7d0' : '#fde68a'}`, borderRadius: 10 }}>
                  {customDomain.verified ? <CheckCircle size={20} color="#16a34a" /> : <Clock size={20} color="#92400e" />}
                  <div>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: customDomain.verified ? '#166534' : '#92400e' }}>
                      {customDomain.domain} — {customDomain.verified ? 'Vérifié' : 'En attente de vérification'}
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
                      {customDomain.verified
                        ? `Emails envoyés depuis : ${params.email_expediteur?.endsWith('@' + customDomain.domain) ? params.email_expediteur : 'notifications@' + customDomain.domain}`
                        : 'Ajoutez les enregistrements DNS ci-dessous, puis cliquez "Vérifier"'}
                    </p>
                  </div>
                  <button onClick={handleDeleteDomain} style={{ marginLeft: 'auto', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    Supprimer
                  </button>
                </div>

                {/* DNS Records */}
                {!customDomain.verified && customDomain.records?.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: '#374151', margin: '0 0 8px' }}>
                      Enregistrements DNS à ajouter chez votre hébergeur :
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {customDomain.records.map((rec, i) => (
                        <div key={i} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px', fontSize: 12, fontFamily: 'monospace' }}>
                          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 4, fontWeight: 700, fontSize: 11 }}>{rec.record}</span>
                            <span><strong>Nom :</strong> {rec.name}</span>
                            {rec.priority && <span><strong>Priorité :</strong> {rec.priority}</span>}
                          </div>
                          <div style={{ marginTop: 6, wordBreak: 'break-all', color: '#374151' }}>
                            <strong>Valeur :</strong> {rec.value}
                          </div>
                          <div style={{ marginTop: 4, color: rec.status === 'verified' ? '#16a34a' : '#9ca3af', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                            {rec.status === 'verified' ? <><CheckCircle size={11} /> Enregistrement détecté</> : '○ En attente'}
                          </div>
                        </div>
                      ))}
                    </div>
                    <button onClick={handleVerifyDomain} disabled={domainLoading}
                      style={{ marginTop: 12, background: '#1e3a8a', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', width: '100%', opacity: domainLoading ? 0.6 : 1 }}>
                      {domainLoading ? 'Vérification...' : "J'ai ajouté les enregistrements — Vérifier"}
                    </button>
                  </div>
                )}

                {customDomain.verified && (
                  <button onClick={handleVerifyDomain} disabled={domainLoading}
                    style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 16px', fontSize: 12, cursor: 'pointer' }}>
                    Rafraîchir le statut
                  </button>
                )}
              </div>
            )}
          </Section>

          {/* Intégrations webhook */}
          <Section title="Intégrations & Webhook" Icon={Link}>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px', lineHeight: 1.6 }}>
              Connectez votre site de réservation à QBDP. À chaque nouvelle réservation, le client est ajouté automatiquement et reçoit un <strong>email d'invitation</strong> pour créer son accès à l'application.
            </p>

            {/* Toggle email bienvenue */}
            {webhook && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#f9fafb', borderRadius: 10, border: '1px solid #e5e7eb', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 6 }}><Mail size={14} /> Email d'invitation automatique</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Le client reçoit un lien pour créer son accès à l'application</div>
                </div>
                <button onClick={() => handleToggleWelcome(!webhook.welcome_email_enabled)}
                  style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', position: 'relative', transition: 'background .2s',
                    background: webhook.welcome_email_enabled ? '#1a56db' : '#d1d5db' }}>
                  <span style={{ position: 'absolute', top: 3, left: webhook.welcome_email_enabled ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                </button>
              </div>
            )}

            {/* Token + URL webhook */}
            {webhook && (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>URL Webhook</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <input readOnly style={{ ...inp, flex: 1, fontSize: 11, background: '#fff', color: '#374151', fontFamily: 'monospace' }}
                    value={`${BASE_URL.replace('/api', '')}/api/webhooks/reservation/${webhook.token}`} />
                  <button onClick={() => copyToClipboard(`${BASE_URL.replace('/api', '')}/api/webhooks/reservation/${webhook.token}`, 'url')}
                    style={{ padding: '0 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: copied === 'url' ? '#f0fdf4' : '#fff', color: copied === 'url' ? '#16a34a' : '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
                    {copied === 'url' ? <><Check size={12} /> Copié</> : <><Clipboard size={12} /> Copier</>}
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>Token intégré dans l'URL — gardez-le secret</div>
                  <button onClick={handleRegenerateToken} disabled={webhookLoading}
                    style={{ fontSize: 12, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <RefreshCw size={12} /> Regénérer
                  </button>
                </div>
              </div>
            )}

            {/* Snippets d'intégration */}
            <button onClick={() => setShowSnippet(s => !s)}
              style={{ fontSize: 12, color: '#1a56db', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
              {showSnippet ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Exemples d'intégration (JS, PHP, cURL)
            </button>
            {showSnippet && webhook && (
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { lang: 'JavaScript (fetch)', key: 'js', code:
`fetch('${BASE_URL.replace('/api', '')}/api/webhooks/reservation/${webhook.token}', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    nom:            'Jean Dupont',
    email:          'jean@example.com',
    telephone:      '+33612345678',
    date_arrivee:   '2024-06-15',
    date_depart:    '2024-06-18',
    numero_chambre: '205'
  })
});` },
                  { lang: 'PHP (cURL)', key: 'php', code:
`$ch = curl_init('${BASE_URL.replace('/api', '')}/api/webhooks/reservation/${webhook.token}');
curl_setopt($ch, CURLOPT_POST, 1);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
  'nom'            => 'Jean Dupont',
  'email'          => 'jean@example.com',
  'telephone'      => '+33612345678',
  'date_arrivee'   => '2024-06-15',
  'date_depart'    => '2024-06-18',
  'numero_chambre' => '205',
]));
curl_setopt($ch, CURLOPT_HTTPHEADER, ['Content-Type: application/json']);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_exec($ch); curl_close($ch);` },
                  { lang: 'cURL (terminal)', key: 'curl', code:
`curl -X POST \\
  '${BASE_URL.replace('/api', '')}/api/webhooks/reservation/${webhook.token}' \\
  -H 'Content-Type: application/json' \\
  -d '{"nom":"Jean Dupont","email":"jean@example.com","date_arrivee":"2024-06-15","date_depart":"2024-06-18","numero_chambre":"205"}'` },
                ].map(s => (
                  <div key={s.key}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>{s.lang}</span>
                      <button onClick={() => copyToClipboard(s.code, s.key)}
                        style={{ fontSize: 11, color: copied === s.key ? '#16a34a' : '#1a56db', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                        {copied === s.key ? <><Check size={11} /> Copié</> : <><Clipboard size={11} /> Copier</>}
                      </button>
                    </div>
                    <pre style={{ background: '#1a1a2e', color: '#e2e8f0', padding: 14, borderRadius: 10, fontSize: 11, overflowX: 'auto', margin: 0, lineHeight: 1.6 }}>{s.code}</pre>
                  </div>
                ))}
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#92400e', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                  <Lightbulb size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>Champs disponibles : <code>nom</code> (requis), <code>email</code>, <code>telephone</code>, <code>date_arrivee</code>, <code>date_depart</code>, <code>numero_chambre</code>, <code>date_naissance</code>, <code>notes</code></div>
                </div>
              </div>
            )}
          </Section>

        </div>

        {/* Aperçu */}
        <div>
          <div style={{ position: 'sticky', top: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Aperçu app mobile</div>
            <Preview />

            {/* Code de connexion admin */}
            <div style={{ marginTop: 16, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
                <Key size={15} color="#6b7280" />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>Code de connexion</span>
              </div>
              <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                background: (isDefault && !isGod) ? '#fef2f2' : '#f0fdf4' }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: (isDefault && !isGod) ? '#991b1b' : '#15803d', display: 'flex', alignItems: 'center', gap: 5 }}>
                    {(isDefault && !isGod)
                      ? <><AlertTriangle size={13} /> Code provisoire</>
                      : <><CheckCircle size={13} /> Code personnalisé</>}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                    {(isDefault && !isGod) ? 'Changez-le — code reçu par email' : 'Votre accès admin est sécurisé'}
                  </div>
                </div>
                <button onClick={() => setShowCode(true)}
                  style={{ background: '#1a56db', color: 'white', border: 'none', borderRadius: 8, padding: '7px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                  <Key size={12} /> Changer
                </button>
              </div>
              {showGodMode && (
                <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, borderTop: '1px solid #f3f4f6' }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: isGod ? '#7c3aed' : '#dc2626', display: 'flex', alignItems: 'center', gap: 5 }}>
                      {isGod ? <><Zap size={13} /> God Mode</> : <><RefreshCw size={13} /> Réinitialiser</>}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                      {isGod ? 'Administration complète' : 'Valeurs QBDP par défaut'}
                    </div>
                  </div>
                  <button onClick={() => setShowReset(true)}
                    style={{ background: isGod ? '#7c3aed' : '#dc2626', color: 'white', border: 'none', borderRadius: 8, padding: '7px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                    {isGod ? <><Zap size={12} /> God Mode</> : 'Réinitialiser'}
                  </button>
                </div>
              )}
            </div>

            <button onClick={handleSave} disabled={saving}
              style={{ marginTop: 16, width: '100%', padding: '14px 0', borderRadius: 14, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14,
                background: '#1a56db', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: '0 4px 24px rgba(26,86,219,0.4)', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Sauvegarde...' : <><Save size={15} /> Sauvegarder les modifications</>}
            </button>
            <button onClick={handleGeocodeAll} disabled={geocodeLoading}
              style={{ marginTop: 10, width: '100%', padding: '11px 0', borderRadius: 12, border: '1px solid #d1d5db', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                background: '#f9fafb', color: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                opacity: geocodeLoading ? 0.7 : 1 }}>
              {geocodeLoading ? 'Géocodage en cours...' : <><MapPin size={14} /> Géocoder les clients existants</>}
            </button>
            {geocodeResult && (
              <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                background: geocodeResult.ok ? '#f0fdf4' : '#fef2f2',
                color: geocodeResult.ok ? '#16a34a' : '#dc2626', textAlign: 'center' }}>
                {geocodeResult.msg}
              </div>
            )}
            <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <a href="https://qbdp.fr/boutique" target="_blank" rel="noreferrer"
                style={{ padding: '11px 0', borderRadius: 12, fontWeight: 600, fontSize: 12,
                  background: '#1a56db', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, textDecoration: 'none' }}>
                <ShoppingBag size={13} color="white" /> Boutique
              </a>
              <a href="https://qbdp.fr" target="_blank" rel="noreferrer"
                style={{ padding: '11px 0', borderRadius: 12, fontWeight: 600, fontSize: 12,
                  background: '#0f172a', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, textDecoration: 'none' }}>
                <Globe size={13} color="white" /> Site
              </a>
              <a href="mailto:contact@qbdp.fr"
                style={{ padding: '11px 0', borderRadius: 12, fontWeight: 600, fontSize: 12,
                  background: '#0f172a', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, textDecoration: 'none' }}>
                <Mail size={13} color="white" /> Contact
              </a>
              <a href="mailto:support@qbdp.fr"
                style={{ padding: '11px 0', borderRadius: 12, fontWeight: 600, fontSize: 12,
                  background: '#0f172a', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, textDecoration: 'none' }}>
                <HeadphonesIcon size={13} color="white" /> Support
              </a>
            </div>
          </div>
        </div>
      </div>

      {confirmModal}
    </div>
  );
}
